import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, SectionHeader } from '../../../components/ui';
import type { SubjectType } from '../../../types/issues';
import { useIssuesForSubject } from '../useIssues';
import { BalanceStrip } from './BalanceBar';
import { PositionPill } from './PositionMark';
import { STATUS_LABEL, decisionPhrase, formatIndex, issueHref, timeAgo } from './format';

/**
 * The issues one entity has a position on, for their profile. It says where
 * they stand, how the issue as a whole is going, and leads into the workspace.
 * When there is nothing to show it renders nothing at all.
 */
export const SubjectIssuesSection: React.FC<{
  subjectType: SubjectType;
  subjectId: string | undefined;
  className?: string;
}> = ({ subjectType, subjectId, className = '' }) => {
  const { rows, loading, failed } = useIssuesForSubject(subjectType, subjectId);

  // A profile is not the place to explain that issues are not deployed
  if (loading || failed || !rows.length) return null;

  return (
    <section className={className}>
      <SectionHeader
        title="Issues"
        aside={
          <span className="tabular-nums">
            {rows.length} position{rows.length === 1 ? '' : 's'}
          </span>
        }
      />
      <Card divided>
        {rows.map(({ issue, stance }) => (
          <Link
            key={issue.id}
            to={issueHref(issue)}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-neutral-50"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] text-ink">{issue.title}</p>
              <p className="mt-0.5 truncate text-[12px] text-neutral-400">
                {STATUS_LABEL[issue.status]}
                {issue.decisionDate ? ` · decided ${decisionPhrase(issue.decisionDate)}` : ''}
                {stance.asOf ? ` · recorded ${timeAgo(stance.asOf)}` : ''}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <BalanceStrip balance={issue.balance} className="w-24 flex-shrink-0 sm:w-32" />
                <span className="text-[11.5px] tabular-nums text-neutral-400" title="Balance of forces">
                  {formatIndex(issue.balance?.index ?? 0)}
                </span>
              </div>
            </div>
            <PositionPill position={stance.position} short className="flex-shrink-0" />
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
          </Link>
        ))}
      </Card>
    </section>
  );
};

export default SubjectIssuesSection;
